// フィードフォルダ（データフィード自動生成）のクライアント側解決ロジック。
// /streams の content.feedProductCandidates（サーバーがキャップ済み・決定的順序で解決した商品候補プール）から、
// 抽選して表示商品を決める（設計書 付録A-2「常にシャッフルする」）。
//
// 重要: フリークエンシー挿入（streamsTargeting.js の pickFrequencyInsertCandidate 等）とは異なり、
// displayedIds/seenIds のような状態保持は一切行わない。shuffle(pool).slice(0, N) のみ。
// ただし抽選結果はページ表示中だけ固定する（resolveFeedFolderProductsOnce のコメント参照）。
// 例外は共有リンク経由の1件だけで、その商品をプールの先頭へ固定する（pinned の扱いを参照）。
import { getCampaignTileColumns, getCampaignTileRows } from '@/helpers/campaignSettings';
import { truncateEventParamValue } from '@/helpers/truncateEventParamValue';
import { getShareParams } from '@/helpers/shareLink';
import {
  buildFeedFolderSaleLabel,
  normalizeFeedFolderSaleLabelColor,
  FEED_FOLDER_SALE_LABEL_DEFAULT_TEXT_COLOR,
  FEED_FOLDER_SALE_LABEL_DEFAULT_BACKGROUND_COLOR,
} from '@/helpers/feedFolderSaleLabel';

const asArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);
const normalizeId = (value) => (value == null ? '' : String(value));

// 商品側の時刻窓（表示停止日・セール期間）をクライアント時刻で評価する。
//
// なぜクライアントで再評価するか: 商品候補と isOnSale はサーバーが解決して /streams に載せるが、
// D1エッジ経路のペイロードは事前計算のため最大1日古くなりうる（再投影は毎晩のGMC同期時）。
// GMCは表示停止日やセール期間が過ぎても商品を返し続けるため、窓を評価しないと
// 「掲載終了した商品」「終わったセールのSALEラベル」がその間ずっと出続ける。
// 窓を持たない商品（サーバーが属性を送っていない）は従来どおり無条件に有効。
const parseWindowTime = (value) => {
  if (!value) return null;
  const time = Date.parse(value);
  return Number.isFinite(time) ? time : null;
};

export const isFeedFolderProductExpired = (product, now = Date.now()) => {
  const expiresAt = parseWindowTime(product?.expirationDate);
  return expiresAt !== null && expiresAt <= now;
};

// GMCの salePriceEffectiveDate は開始を含み終了を含まない（google.type.Interval）。
// API側の MerchantProduct#sale_price_effective? と同じ境界にそろえる
export const isFeedFolderProductOnSale = (product, now = Date.now()) => {
  if (!product?.isOnSale) return false;

  const startAt = parseWindowTime(product?.salePriceEffectiveStartAt);
  if (startAt !== null && now < startAt) return false;

  const endAt = parseWindowTime(product?.salePriceEffectiveEndAt);
  if (endAt !== null && now >= endAt) return false;

  return true;
};

// 表示可能な候補プール。表示件数の計算と抽選の両方がこれを使う
// （片方だけで間引くと「N件出す設定なのに期限切れの分だけ欠ける」ズレになる）
const getFeedFolderPool = (content, now = Date.now()) => (
  asArray(content?.feedProductCandidates).filter((product) => !isFeedFolderProductExpired(product, now))
);

export const FEED_FOLDER_CONTENT_PREFIX = 'feed-folder';
export const FEED_FOLDER_GROUP_PREFIX = 'feed-folder-group';

// 表示スロットの仮想ID。共有リンク（openSharedContent）は「商品を先頭に固定したときのID」を
// 事前に組み立てて照合するため、生成規則を1箇所に集約して文字列のズレを防ぐ。
export const buildFeedFolderContentId = (contentId, feedProductId, sequence) => (
  `${FEED_FOLDER_CONTENT_PREFIX}-${normalizeId(contentId)}-${normalizeId(feedProductId)}-${sequence}`
);

// 既読（contentSeen）のキー。表示スロットの仮想IDは末尾に並び順を持ち、ページ表示ごとの抽選で変わるため、
// そのまま保存するとリロード後に同じ商品が別の並び順で出ただけで未読に戻る。並び順を外し、
// 「どの連携配信の、どの商品を見たか」で覚える。仮想ID以外（通常コンテンツ）はそのまま返す。
const FEED_FOLDER_CONTENT_ID_PATTERN = new RegExp(`^(${FEED_FOLDER_CONTENT_PREFIX}-.+-.+)-\\d+$`);

export const toContentSeenKey = (contentId) => {
  const id = normalizeId(contentId);
  if (id.startsWith(`${FEED_FOLDER_GROUP_PREFIX}-`)) return id;
  return id.match(FEED_FOLDER_CONTENT_ID_PATTERN)?.[1] ?? id;
};

export const buildFeedFolderGroupId = (contentId, feedProductId, index) => (
  `${FEED_FOLDER_GROUP_PREFIX}-${normalizeId(contentId)}-${normalizeId(feedProductId)}-${index}`
);

// タイル型・リール型は「1セル/1サムネイル＝1グループ」のため、フィードフォルダを
// content ではなく content_group 側へ仮想展開する（streamsTargeting.js と同じ判定）。
// 共有リンク側も同じ分岐で復元先IDを決める必要があるため、判定をここで共有する。
export const expandsFeedFolderAsGroups = (campaign) => (
  campaign?.frame === 'tile' || campaign?.frame === 'square'
);

// contents テーブルのCTA既定値（cta_background_color_code: #FFFFFF / cta_text_color_code: #000000）。
// feed_folder の content は cta_* をすべて null で保存する規約（DD-2）のため、ここで補完しないと
// ctaButton.js のフォールバック（#272727 の暗い背景）になり、通常コンテンツと見た目が食い違う。
// 透過度・文字サイズは通常コンテンツでも既定が null（ctaButton.js 側の既定値が効く）なので補完しない。
export const FEED_FOLDER_DEFAULT_CTA_BACKGROUND_COLOR_CODE = '#FFFFFF';
export const FEED_FOLDER_DEFAULT_CTA_TEXT_COLOR_CODE = '#000000';
// CTAの文字サイズは library の既定（ctaButton.js の 18px）ではなく 16px に揃える。
// feed_folder の content は cta_font_size が常に null（API側でロック）のため、ここで補完しないと既定の18pxになる。
export const FEED_FOLDER_DEFAULT_CTA_FONT_SIZE = 16;

// 表示件数を決める（付録A-2の仕様どおり）。
// reel/story: displayCount（プール件数で頭打ち）
// tile: campaign.tileRows × campaign.tileColumns（プール件数で頭打ち）
export const pickFeedFolderDisplayCount = (content, campaign) => {
  const pool = getFeedFolderPool(content);

  if (campaign?.frame === 'tile') {
    const n = getCampaignTileRows(campaign) * getCampaignTileColumns(campaign);
    return Math.min(n, pool.length);
  }

  const filter = content?.feedFolderFilter || {};
  const displayCount = Number(filter.displayCount);
  const requested = Number.isFinite(displayCount) && displayCount > 0 ? displayCount : 0;
  return Math.min(requested, pool.length);
};

// Fisher-Yates シャッフル（非破壊、元配列は変更しない）
export const shuffleFeedFolderPool = (pool) => {
  const result = asArray(pool);
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

// 抽選結果をページ表示中は固定するためのメモ置き場（idを持たない sessionOwner 用のフォールバック）。
const FEED_FOLDER_SELECTION_KEY = '__storeeelFeedFolderSelection';

// SPA（history API）でURLが変わると /streams を取り直し、window.storeeelData のキャンペーンが
// 「同じidの別オブジェクト」に差し替わる。抽選メモを campaign オブジェクトにだけ持たせていると
// この瞬間にメモが消えて再抽選が走り、すでに描画済みのタイルID と食い違ってクリックが無反応になる。
// オブジェクトの同一性ではなくキャンペーンIDで引けるよう、モジュールスコープのMapに保持する
// （window等のグローバルは汚さない）。IDを持たない sessionOwner は従来どおりオブジェクトに持たせる。
const feedFolderSelectionByCampaignId = new Map();

const getFeedFolderSelectionStore = (sessionOwner) => {
  const campaignId = normalizeId(sessionOwner?.id);
  if (campaignId) {
    if (!feedFolderSelectionByCampaignId.has(campaignId)) {
      feedFolderSelectionByCampaignId.set(campaignId, {});
    }
    return feedFolderSelectionByCampaignId.get(campaignId);
  }

  // cloneValue で複製されない置き場所にするため enumerable にしない
  // （クローン時に持ち回られると固定が効かなくなる）
  if (!sessionOwner[FEED_FOLDER_SELECTION_KEY]) {
    Object.defineProperty(sessionOwner, FEED_FOLDER_SELECTION_KEY, {
      value: {},
      enumerable: false,
      writable: true,
      configurable: true,
    });
  }
  return sessionOwner[FEED_FOLDER_SELECTION_KEY];
};

// 共有リンクで指定された商品ID（この content 宛てのものだけ）を返す。
// 共有URLの storeeel_content_id はフィードの場合「展開元の feed_folder content の id」なので、
// 対象の content にだけピン留めが効き、同一ページ内の別フィードフォルダには影響しない。
export const getPinnedFeedProductId = (content) => {
  const params = getShareParams();
  if (!params?.feedProductId || !params.contentId) return null;
  if (normalizeId(params.contentId) !== normalizeId(content?.id)) return null;
  return params.feedProductId;
};

// ページ表示ごとに1回だけ抽選し、同じ campaign に対しては同じ商品セット・同じ順序を返す。
//
// 描画のたびに抽選し直すと、タイル型/リール型の仮想グループID（`feed-folder-group-{contentId}-
// {商品ID}-{並び順}`）が毎回変わる。DOMに描かれたタイルのIDと prepared キャッシュのIDが食い違い、
// campaignIconsTapAction の `if (!selectedGroup) return;` に落ちて、クリックしても何も起きない
// （エラーもログも出ない）状態になる。実際にタイル型で発生した不具合の根本原因。
//
// sessionOwner を渡さない場合は従来どおり毎回抽選する（後方互換）。
export const resolveFeedFolderProductsOnce = (content, campaign, sessionOwner) => {
  const contentId = normalizeId(content?.id);
  const pinnedProductId = getPinnedFeedProductId(content);
  if (!sessionOwner || !contentId) return resolveFeedFolderProducts(content, campaign, pinnedProductId);

  const selection = getFeedFolderSelectionStore(sessionOwner);
  if (!Array.isArray(selection[contentId])) {
    selection[contentId] = resolveFeedFolderProducts(content, campaign, pinnedProductId);
  }
  return selection[contentId];
};

// pinnedProductId を渡すと、その商品だけを先頭に固定して残りは従来どおりシャッフルする
// （共有リンクから来た人に、共有された商品を必ず・最初に見せるため）。
// 未指定、または商品がプールから消えている場合（GMC同期で掲載終了・表示停止日切れ）は
// 完全に従来どおりの抽選になる。
export const resolveFeedFolderProducts = (content, campaign, pinnedProductId = null) => {
  const pool = getFeedFolderPool(content);
  const n = pickFeedFolderDisplayCount(content, campaign);
  const shuffled = shuffleFeedFolderPool(pool);

  const pinnedIndex = pinnedProductId == null
    ? -1
    : shuffled.findIndex((product) => normalizeId(product?.id) === normalizeId(pinnedProductId));
  if (pinnedIndex > 0) {
    // シャッフル後に先頭へ移動する。ピン留め以外の並びはランダムのまま保たれる
    const [pinned] = shuffled.splice(pinnedIndex, 1);
    shuffled.unshift(pinned);
  }

  return shuffled.slice(0, n);
};

// フィードフォルダ商品を、既存の静止画コンテンツ描画パイプライン（background-image方式）に
// そのまま乗せるための擬似 mediaAsset を合成する。
// mediaAsset.id は `{content hashid}:{merchant_products.id}` の複合IDにする。既存の「非freq通常content」の
// mediaId解決（content.mediaAsset?.id）がそのまま通常イベント（storeeel_media_asset_view 等）に載り、
// API 側は前半の hashid でコンテンツを、後半の商品IDで行を特定して商品単位に集計できる。
// hashid が取れない場合（旧キャッシュのD1ペイロード等）は空文字にする。商品IDを裸で送ると
// API 側の decode_record_id が MediaAsset の主キーとして解決し、無関係なコンテンツに加算されるため。
const buildFeedFolderMediaId = (contentHashid, productId) => {
  const hashid = normalizeId(contentHashid);
  const id = normalizeId(productId);
  if (!hashid || !id) return '';
  return `${hashid}:${id}`;
};

// hashid が無いと表示・CTAクリック・CVのすべてが計測されないが、描画は正常に見えるため
// 「配信できているのにレポートだけ0件」という気付きにくい状態になる。
// 原因（配信ペイロードが hashid 追加前の古い投影のまま等）に辿り着けるよう警告を残す。
// 描画のたびに出ると邪魔なので content ごとに1回だけ出す。
const warnedMissingHashidContentIds = new Set();

const warnMissingContentHashid = (contentId) => {
  if (warnedMissingHashidContentIds.has(contentId)) return;
  warnedMissingHashidContentIds.add(contentId);
  console.warn(
    '[storeeel] 連携配信の content.hashid が取得できないため、表示・クリック・CVを計測しません。'
    + '配信ペイロードが古い可能性があります（content id: '
    + `${contentId || '不明'})`
  );
};

const buildFeedFolderMediaAsset = (product, contentHashid) => ({
  id: buildFeedFolderMediaId(contentHashid, product?.id),
  type: 'mediaAsset',
  // storeeel_name の生成元はここ1箇所。GA4のパラメータ値上限(100文字)に収まるよう丸める
  // （GMCの商品タイトルは最大150文字。管理画面の表示はAPIがmerchant_products.titleから引くため影響しない）
  name: truncateEventParamValue(product?.title || ''),
  mediaUrl: product?.imageLink || '',
  // resolveClosedCampaignThumbnail / commonChangeMediaProcess は mime_type を前提にするため、
  // 静止画として扱われるダミーの mime_type を合成する（動画は存在しない、DD-10）
  attachmentData: { metadata: { mime_type: 'image/jpeg' } },
});

// 商品(products)を表示用content互換オブジェクトへ変換する。
// - CTAテキスト = title、CTAリンク = link（既存の単品追加フロー ContentApplier と同一規約）
// - 画像は background-image 方式（icons.js の既存パターン）に合わせるため mediaAsset.mediaUrl に imageLink を設定
// - 表示スロットの一時ID（DOM識別用） と 安定ID（トラッキング用、feedProductId） を分離する
export const buildFeedFolderDisplayContents = (content, products) => {
  const contentId = normalizeId(content?.id);
  // トラッキング用の複合IDに使う content hashid（/streams の content attributes に載る）
  const contentHashid = content?.hashid;
  if (!normalizeId(contentHashid)) warnMissingContentHashid(contentId);
  const contentGroupId = content?.contentGroupId;
  const nextRefreshAt = content?.nextRefreshAt ?? null;
  // SALEラベルの表示可否。管理画面で「セール中の商品のみ」＋「SALEラベルを表示する」を
  // 選んだときだけ true になる。このキーを持たない既存のフィードフォルダは false 扱い。
  const showSaleLabelEnabled = Boolean(content?.feedFolderFilter?.showSaleLabel);
  // ラベルの表示形式（sale / price / percent）。未指定の既存データは 'sale' に倒れる
  const saleLabelType = content?.feedFolderFilter?.saleLabelType;
  // ラベルの配色。管理画面で選んだ色をそのまま使う。配色を持たない既存データや
  // 色として解釈できない値は、従来の見た目（白文字＋オレンジ）へ倒す
  const saleLabelTextColor = normalizeFeedFolderSaleLabelColor(
    content?.feedFolderFilter?.saleLabelTextColor,
    FEED_FOLDER_SALE_LABEL_DEFAULT_TEXT_COLOR,
  );
  const saleLabelBackgroundColor = normalizeFeedFolderSaleLabelColor(
    content?.feedFolderFilter?.saleLabelBackgroundColor,
    FEED_FOLDER_SALE_LABEL_DEFAULT_BACKGROUND_COLOR,
  );

  return asArray(products)
    .map((product, sequence) => {
      const feedProductId = normalizeId(product?.id);
      if (!feedProductId) return null;

      const mediaAsset = buildFeedFolderMediaAsset(product, contentHashid);
      // セール期間はサーバーが焼いた isOnSale をそのまま信じず、描画時刻で評価し直す
      const onSale = isFeedFolderProductOnSale(product);

      return {
        id: buildFeedFolderContentId(contentId, feedProductId, sequence),
        isFeedFolderContent: true,
        // 展開元の feed_folder content の id。仮想IDと違い抽選で変わらないため、
        // 共有URLにはこちらを storeeel_content_id として載せる（sharingClickAction）
        feedSourceContentId: contentId,
        // 描画側（createSlide.js / icons.js）はこのフラグだけを見ればよいよう、
        // 「設定がON」かつ「実際にセール中」をここで畳んでおく
        showSaleLabel: showSaleLabelEnabled && onSale,
        // 出す中身もここで確定させる。描画側は受け取ったパーツを組むだけにして、
        // 価格の整形や割引率の計算が3箇所（スライド本体・square/tileサムネ・ストーリー型サムネ）へ散らないようにする。
        // ストーリー型サムネだけは形式によらず 'SALE' 固定のため、この値を使わない
        saleLabel: buildFeedFolderSaleLabel(product, saleLabelType),
        // 配色は形式によらず全ての描画経路（スライド本体・ストーリー型サムネ）で同じものを使う
        saleLabelTextColor,
        saleLabelBackgroundColor,
        feedProductId,
        position: sequence,
        contentGroupId,
        mediaAsset,
        ctaLink: product?.link || '',
        ctaText: product?.title || '',
        // フィード由来コンテンツのCTAスタイルは content 側の指定を踏襲し、未指定なら
        // contents の既定値（白背景・黒文字）へフォールバックする。
        // 現状のAPIは feed_folder の cta_* を必ず null で返す（Content::FEED_FOLDER_LOCKED_ATTRIBUTES）が、
        // ロックが外れたときに既定値で上書きしないよう content 側を優先したままにしておく。
        ctaBackgroundColorCode: content?.ctaBackgroundColorCode ?? FEED_FOLDER_DEFAULT_CTA_BACKGROUND_COLOR_CODE,
        ctaBackgroundOpacity: content?.ctaBackgroundOpacity,
        ctaTextColorCode: content?.ctaTextColorCode ?? FEED_FOLDER_DEFAULT_CTA_TEXT_COLOR_CODE,
        ctaFontSize: content?.ctaFontSize ?? FEED_FOLDER_DEFAULT_CTA_FONT_SIZE,
        feedFolderProduct: {
          id: feedProductId,
          offerId: product?.offerId || '',
          title: product?.title || '',
          brand: product?.brand || '',
          link: product?.link || '',
          imageLink: product?.imageLink || '',
          priceAmount: product?.priceAmount ?? null,
          priceCurrency: product?.priceCurrency ?? null,
          salePriceAmount: product?.salePriceAmount ?? null,
          isOnSale: onSale,
        },
        nextRefreshAt,
      };
    })
    .filter(Boolean);
};

// タイル型・リール型: フィードフォルダの1contentを「1商品のみを持つ仮想content_group」N個に展開する。
// タイル型は1セル＝1グループ、リール型は1サムネイル＝1グループ（先頭コンテンツのみ描画）のため、
// 1グループ内にN件のcontentを持たせると閉じた状態で1件しか出ない。グループ側をN個に展開して初めて
// 「表示件数分のタイル/サムネイルが並ぶ」状態になる。
// generateTileRows（icons.js）は渡された contentGroups を機械的に tileColumns 件ずつ詰め直すだけで
// 中身が本物か仮想かを区別しないため、tileRow/tileCol は 0 固定でよい（安定ソートにより配列順＝
// シャッフル済み抽選順が保持される。§6.4-5）。
// options.name: リール型はサムネイル直下にグループ名を表示するため、展開元グループの名前を引き継ぐ
// （未指定時は商品名。タイル型はグループ名を表示しないため既定のままでよい）。
export const buildVirtualGroupsForFeedFolder = (content, campaign, options = {}) => {
  const products = resolveFeedFolderProductsOnce(content, campaign, options.sessionOwner);
  const contentId = normalizeId(content?.id);

  return buildFeedFolderDisplayContents(content, products).map((displayContent, index) => {
    const groupId = buildFeedFolderGroupId(contentId, displayContent.feedProductId, index);
    return {
      id: groupId,
      name: options.name ?? (displayContent.ctaText || ''),
      tileRow: 0,
      tileCol: 0,
      contents: [{ ...displayContent, contentGroupId: groupId }],
    };
  });
};
